import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  Divider,
  HStack,
  IconButton,
  Spinner,
  Text,
  VStack,
  Image,
} from "@chakra-ui/react";
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from "react-zoom-pan-pinch";
import colors from "app/colors";
import { ResetZoomIcon, ZoomInIcon, ZoomOutIcon } from "modules/icons";
import { FileGalleryFragment } from "gql/graphql";
import useResizeObserver from "modules/hooks/use-resize-observer";

interface IProps {
  file: FileGalleryFragment;
}

const FileImage = ({ file }: IProps) => {
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const {
    elementRef: containerRef,
    height: containerHeight,
    width: containerWidth,
  } = useResizeObserver();

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    },
    [],
  );

  const fitScale = useMemo(() => {
    if (
      imageSize.width === 0 ||
      imageSize.height === 0 ||
      containerWidth === 0 ||
      containerHeight === 0
    ) {
      return 1;
    }
    return Math.min(
      containerWidth / imageSize.width,
      containerHeight / imageSize.height,
    );
  }, [containerHeight, containerWidth, imageSize]);

  const imageTransformRef = useRef<ReactZoomPanPinchRef>(null);
  const [imageZoomScale, setImageZoomScale] = useState(1);

  const displayZoom = useMemo(
    () => fitScale * imageZoomScale,
    [imageZoomScale, fitScale],
  );

  const zoomInImage = useCallback(() => {
    imageTransformRef.current?.zoomIn();
  }, []);

  const zoomOutImage = useCallback(() => {
    imageTransformRef.current?.zoomOut();
  }, []);

  const resetImageZoom = useCallback(() => {
    imageTransformRef.current?.resetTransform();
  }, []);

  const maxScale = 5;
  const minScale = 0.25;

  const isZoomInDisabled = imageZoomScale >= maxScale;
  const isZoomOutDisabled = imageZoomScale <= minScale;
  const isZoomResetDisabled = imageZoomScale === 1;

  if (!file || !file.cloudFrontUrl) {
    return <Spinner />;
  }

  return (
    <VStack
      spacing={0}
      justifyItems="center"
      alignItems="center"
      w="full"
      flex={1}
      minH={0}
      overflow="hidden"
    >
      <HStack
        borderColor={colors.gray50}
        borderWidth={1}
        rounded={4}
        p={0.5}
        mt={3}
        mb={2}
        spacing={0.5}
        flexShrink={0}
      >
        <IconButton
          h={6}
          minW={6}
          rounded={4}
          bg="unset"
          _hover={{ bg: colors.gray40 }}
          _active={{ bg: colors.gray50 }}
          icon={<ZoomOutIcon boxSize={6} fill={colors.white} />}
          title="Zoom out"
          aria-label="Zoom out"
          isDisabled={isZoomOutDisabled}
          onClick={zoomOutImage}
          variant="hover"
        />
        <Text color="white" fontSize="sm" px={2} w="60px" align="center">
          {Math.round(displayZoom * 100)}%
        </Text>
        <IconButton
          h={6}
          minW={6}
          rounded={4}
          bg="unset"
          _hover={{ bg: colors.gray40 }}
          _active={{ bg: colors.gray50 }}
          icon={<ZoomInIcon boxSize={6} fill={colors.white} />}
          title="Zoom in"
          aria-label="Zoom in"
          isDisabled={isZoomInDisabled}
          onClick={zoomInImage}
          variant="hover"
        />
        <Divider orientation="vertical" h={6} borderColor={colors.gray50} />
        <IconButton
          h={6}
          minW={6}
          rounded={4}
          bg="unset"
          _hover={{ bg: colors.gray40 }}
          _active={{ bg: colors.gray50 }}
          icon={<ResetZoomIcon boxSize={4} fill={colors.white} />}
          title="Reset zoom"
          aria-label="Reset zoom"
          onClick={resetImageZoom}
          isDisabled={isZoomResetDisabled}
          variant="hover"
        />
      </HStack>

      {/* flex={1} + minH={0} lets this box fill remaining space without overflow */}
      <Box ref={containerRef} flex={1} minH={0} w="full" overflow="hidden">
        <TransformWrapper
          ref={imageTransformRef}
          onTransformed={(_ref, state) => {
            setImageZoomScale(state.scale);
          }}
          maxScale={maxScale}
          minScale={minScale}
          centerZoomedOut
          centerOnInit
          limitToBounds
        >
          <TransformComponent
            wrapperStyle={{
              height: "100%",
              width: "100%",
            }}
            contentStyle={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Image
              maxH="100%"
              maxW="100%"
              objectFit="contain"
              src={file.cloudFrontUrl}
              onLoad={onImageLoad}
            />
          </TransformComponent>
        </TransformWrapper>
      </Box>
    </VStack>
  );
};

export default memo(FileImage);
